import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Pagination, Select, Space, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { fetchShipments, ShipmentProjection } from '../api/kcc';
import CarrierTrackingNo from '../components/CarrierTrackingNo';
import { useNarrowScreen } from '../hooks/useNarrowScreen';
import { SHIPMENT_STATUS_OPTIONS, statusLabel } from '../utils/shipmentStatus';
import { formatTransportMode } from '../utils/transportMode';

const { Title, Text } = Typography;

const DESKTOP_PAGE_SIZE = 20;
const MOBILE_PAGE_SIZE = 10;

const ShipmentListPage: React.FC = () => {
  const navigate = useNavigate();
  const narrow = useNarrowScreen();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string | undefined>();
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(DESKTOP_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState<ShipmentProjection[]>([]);

  useEffect(() => {
    const next = narrow ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE;
    setSize((prev) => (prev === next ? prev : next));
    setPage(0);
  }, [narrow]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchShipments({ status, page, size });
      setRows(data.content || []);
      setTotal(data.totalElements || 0);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to load shipments');
    } finally {
      setLoading(false);
    }
  }, [status, page, size]);

  useEffect(() => {
    load();
  }, [load]);

  const columns: ColumnsType<ShipmentProjection> = [
    {
      title: 'Shipment ID',
      dataIndex: 'shipmentId',
      key: 'shipmentId',
      render: (v: string) => <a onClick={() => navigate(`/shipments/${encodeURIComponent(v)}`)}>{v}</a>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (v: string) => <Tag>{statusLabel(v)}</Tag>,
    },
    {
      title: 'Shipping way',
      dataIndex: 'transportMode',
      key: 'transportMode',
      render: (v?: string) => formatTransportMode(v),
    },
    {
      title: 'Carrier',
      dataIndex: 'carrierName',
      key: 'carrierName',
      render: (v?: string) => v || '—',
    },
    {
      title: 'Carrier Tracking No.',
      dataIndex: 'channelShipmentId',
      key: 'channelShipmentId',
      ellipsis: true,
      render: (_: string, r) => <CarrierTrackingNo shipment={r} />,
    },
    { title: 'Cartons', dataIndex: 'cartonCount', key: 'cartonCount', width: 90 },
  ];

  const filter = (
    <Select
      allowClear
      placeholder="Filter by status"
      style={{ minWidth: narrow ? '100%' : 220, width: narrow ? '100%' : 220 }}
      value={status}
      onChange={(v) => {
        setPage(0);
        setStatus(v);
      }}
      options={SHIPMENT_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
    />
  );

  return (
    <div>
      <Space
        style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}
        wrap
        direction={narrow ? 'vertical' : 'horizontal'}
      >
        <Title level={3} style={{ margin: 0 }}>
          Shipments
        </Title>
        {filter}
      </Space>
      {narrow ? (
        <div>
          {rows.map((r) => (
            <Card
              key={r.shipmentId}
              size="small"
              loading={loading}
              style={{ marginBottom: 12 }}
              onClick={() => navigate(`/shipments/${encodeURIComponent(r.shipmentId)}`)}
              bodyStyle={{ cursor: 'pointer' }}
            >
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Text strong style={{ wordBreak: 'break-word' }}>{r.shipmentId}</Text>
                  <Tag>{statusLabel(r.status)}</Tag>
                </Space>
                <Text type="secondary">{formatTransportMode(r.transportMode)} · {r.carrierName || '—'}</Text>
                <div onClick={(e) => e.stopPropagation()}>
                  <CarrierTrackingNo shipment={r} />
                </div>
                <Text type="secondary">Cartons: {r.cartonCount ?? '—'}</Text>
              </Space>
            </Card>
          ))}
          {!loading && rows.length === 0 && (
            <Card size="small">
              <Text type="secondary">No shipments</Text>
            </Card>
          )}
          <Pagination
            style={{ marginTop: 8, textAlign: 'center' }}
            simple
            current={page + 1}
            pageSize={size}
            total={total}
            onChange={(p, ps) => {
              setPage(p - 1);
              setSize(ps);
            }}
          />
        </div>
      ) : (
        <Table
          rowKey="shipmentId"
          loading={loading}
          columns={columns}
          dataSource={rows}
          scroll={{ x: 800 }}
          pagination={{
            current: page + 1,
            pageSize: size,
            total,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setPage(p - 1);
              setSize(ps);
            },
          }}
          onRow={(record) => ({
            onClick: () => navigate(`/shipments/${encodeURIComponent(record.shipmentId)}`),
            style: { cursor: 'pointer' },
          })}
        />
      )}
    </div>
  );
};

export default ShipmentListPage;
